import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { message } from "antd";

const SelectPartner = ({
  onSelectPartner,
  selectedValue,
  isDisabled = false,
}) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  // Built-in booking sources. Negative IDs are reserved for non-partner sources so
  // they cannot collide with partner IDs returned by the API.
  const builtInSources = [
    { value: "-101", label: "Airbnb" },
    { value: "-102", label: "MMT" },
    { value: "-103", label: "Google / Website (Direct)" },
    { value: "-104", label: "Repeat (Direct)" },
    { value: "-105", label: "Referral / Whatsapp Group" },
    { value: "0", label: "Walk-In" },
    { value: "-106", label: "Instagram" },
    { value: "-107", label: "Other" },
    { value: "1", label: "Corporate" },
  ];

  const normalizeSourceLabel = (label = "") =>
    label.trim().toLowerCase().replace(/\s+/g, " ");

  const mergeBookingSources = (activePartners = []) => {
    const partnerByLabel = new Map(
      activePartners.map((partner) => [normalizeSourceLabel(partner.label), partner])
    );

    // Prefer a configured partner record when it already exists with the same name.
    // This preserves the real partner ID for OTA/channel integrations.
    const preferred = builtInSources.map((source) => {
      const configuredPartner = partnerByLabel.get(normalizeSourceLabel(source.label));
      if (configuredPartner) {
        partnerByLabel.delete(normalizeSourceLabel(source.label));
        return configuredPartner;
      }
      return source;
    });

    // Keep any other active partners already configured by the property.
    return [...preferred, ...partnerByLabel.values()];
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/hms-partners/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && Array.isArray(response.data.partners)) {
        // Filter for active partners only
        const activePartners = response.data.partners
          .filter((partner) => partner.status === "active")
          .map((partner) => ({
            value: partner.id.toString(),
            label: partner.name,
            partnerDetails: partner,
          }));

        setPartners(mergeBookingSources(activePartners));
      } else {
        throw new Error("Invalid response format: 'partners' array not found");
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      setError("Failed to load partners. Please try again.");
      setPartners(mergeBookingSources([])); // Fallback to built-in sources
      message.error("Error fetching partners.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select
      options={partners}
      onChange={(selectedOption) => {
        // Pass both value and label as an object
        onSelectPartner({
          value: selectedOption ? selectedOption.value : null,
          name: selectedOption ? selectedOption.label : null,
        });
      }}
      value={partners.find((option) => option.value === selectedValue?.value) || partners[0] || null}
      placeholder="Choose booking source"
      isLoading={loading}
      isDisabled={isDisabled || loading || !!error}
      noOptionsMessage={() =>
        error ? error : loading ? "Loading..." : "No partners available"
      }
      isClearable
    />
  );
};

export default SelectPartner;